import { Seaport } from "@opensea/seaport-js";
import { OrderWithCounter } from "@opensea/seaport-js/lib/types";
import axios from "axios";
import { ethers } from "ethers";
import { forwardRef, useState } from "react";
import styled from "styled-components";
import ERC20Artifact from "../artifacts/@openzeppelin/contracts/token/ERC20/IERC20.sol/IERC20.json";
import CollectibleNFTArtifact from "../artifacts/contracts/CollectibleNFT.sol/CollectibleNFT.json";
import ComposeNFTArtifact from "../artifacts/contracts/ComposeNFT.sol/ComposeNFT.json";
import Listing from "../common/models/Listing";
import { User } from "../common/models/User";
import CloseCircle from "../components/Buttons/CloseCircle";
import SimpleButton from "../components/Buttons/SimpleButton";
import Column from "../components/Column/Column";
import { Modal } from "../components/Modal/Modal";
import {
  CircularSVG,
  CustomCircle
} from "../components/ProgressBar/CircluarSVG";
import Row from "../components/Row/Row";
import { BlockChainErrorText } from "../components/Text/BlockchainErrorText";
import { ProgressText } from "../components/Text/ProgressText";
import Text from "../components/Text/Text";
import { useAuth } from "../hooks/useAuth";
import close_circle from "../imgs/close-circle.svg";
import eth_icon from "../imgs/eth_icon_blue.svg";
import { getSelectedProvider, sleep } from "../utils";
import Song, { SongType } from "./models/Song";

interface SongCheckoutModalProps {
  artist: User;
  song: Song;
  onCheckoutCloseClick: any;
  onCheckoutSuccess: any;
  listing: Listing;
}

const SongCheckoutContainer = styled("div")`
  display: flex;
  border: 1px solid #ffffff90;
  border-radius: 12px;
`;

const SongCheckoutImg = styled("img")`
  width: 200px;
  height: 150px;
  position: relative;
  display: inline-block;
  overflow: hidden;
  margin: 0;
  border-radius: 12px;
`;

const CheckoutPriceContainer = styled("div")`
  color: #03bbf5;
  border: 1px solid #03bbf5;
  border-radius: 8px;
  padding: 0.5rem;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-left: auto;
`;

const SongCheckoutContainerModal = styled.div`
  position: relative;
  text-align: left;
  z-index: 3;
  border-radius: 20px;
  padding-right: 6rem;
  width: 30%;
`;

const ContentColumn = styled(Column)`
  gap: 1rem;
  width: 100%;
  border-radius: 20px;
  padding-inline: 3rem;
  padding-block: 10%;
  background: #000d30;
  border: 1px solid #ffffff90;
`;

const SongCheckoutModal = forwardRef<HTMLDivElement, SongCheckoutModalProps>(
  ({ artist, song, onCheckoutCloseClick, onCheckoutSuccess, listing }, ref) => {
    const auth = useAuth();

    const [placingOrder, setPlacingOrder] = useState(false);
    const [progressText, setProgressText] = useState("");
    const [errorText, setErrorText] = useState("");
    const wrappedEthTestNetAddress =
      "0xA6FA4fB5f76172d178d61B04b0ecd319C5d1C0aa";
    const wrappedEthMainnetAddress =
      "0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619";

    const useMainnet: Boolean =
      process.env.REACT_APP_USE_POLYGON_MAINNET === "true";
    const wrappedEthAddress = useMainnet
      ? wrappedEthMainnetAddress
      : wrappedEthTestNetAddress;

    const onPlaceOrder = async () => {
      setPlacingOrder(!placingOrder);
      const selectedProvider = getSelectedProvider();
      const provider = new ethers.providers.Web3Provider(selectedProvider);
      if (song.type === SongType.MASTER_COPY) {
        buyMasterCopy(provider);
      } else if (song.type === SongType.COLLECTIBLE) {
        mint(provider);
      } else {
        buySongToken(provider);
      }
    };

    const buyMasterCopy = async (provider) => {
      const newSeaport = new Seaport(provider);
      const buyerAddress = await provider.getSigner().getAddress();
      setProgressText("Please complete your order through wallet extension.");
      let transaction = null;
      try {
        const { actions } = await newSeaport.fulfillOrder({
          order: listing.getSeaportListing() as OrderWithCounter,
          accountAddress: buyerAddress,
        });

        const action = actions[0];
        setProgressText("Loading...");
        transaction = await action.transactionMethods.transact();
        await transaction.wait();
      } catch (error) {
        if (
          error
            .toString()
            .includes(
              "The fulfiller does not have the balances needed to fulfill"
            )
        ) {
          setErrorText("Insufficient Funds");
        } else {
          setErrorText("Something wrong, please try again.");
        }
        setPlacingOrder(false);
        return;
      }

      // Check every second to see if the contract has been deployed
      while (true) {
        await sleep(1000);
        const currentTransaction = await provider.getTransaction(
          transaction.hash
        );
        if (
          currentTransaction === null ||
          currentTransaction.blockNumber !== null
        ) {
          // Transaction has been deployed when there is a block number associated with it
          break;
        }
      }

      const formData = new FormData();
      formData.append("song_id", song.id.toString());
      formData.append("new_owner_address", auth.user.wallet_address);
      axios
        .post(
          process.env.REACT_APP_BACKEND_URL + "/buy_master_copy",
          formData,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
        .then((response) => {
          onCheckoutSuccess();
        });
    };

    const buySongToken = async (provider) => {
      const newSeaport = new Seaport(provider);
      const buyerAddress = await provider.getSigner().getAddress();
      setProgressText("Please complete your order through wallet extension.");

      let transaction = null;
      try {
        const { actions } = await newSeaport.fulfillOrder({
          order: listing.getSeaportListing() as OrderWithCounter,
          accountAddress: buyerAddress,
        });
        console.log("actions are ", actions);

        const action = actions[0];
        setProgressText("Loading...");
        transaction = await action.transactionMethods.transact();
        await transaction.wait();
      } catch (error) {
        if (
          error
            .toString()
            .includes(
              "The fulfiller does not have the balances needed to fulfill"
            )
        ) {
          setErrorText("Insufficient Funds");
        } else {
          setErrorText("Something wrong, please try again.");
        }
        setPlacingOrder(false);
        return;
      }

      console.log("song.nft_address is ", song.nft_address);

      // Check every second to see if the contract has been deployed
      while (true) {
        await sleep(1000);
        const currentTransaction = await provider.getTransaction(
          transaction.hash
        );
        if (
          currentTransaction === null ||
          currentTransaction.blockNumber !== null
        ) {
          // Transaction has been deployed when there is a block number associated with it
          break;
        }
      }

      const formData = new FormData();
      formData.append("song_token_id", song.id.toString());
      formData.append("new_owner_address", auth.user.wallet_address);
      axios
        .post(process.env.REACT_APP_BACKEND_URL + "/buy_song_token", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          onCheckoutSuccess();
        });
    };

    const mint = async (provider) => {
      const collectibleNFTContract = new ethers.Contract(
        song.nft_address,
        CollectibleNFTArtifact.abi,
        provider.getSigner()
      );

      const wrappedEthContract = new ethers.Contract(
        wrappedEthAddress,
        ERC20Artifact.abi,
        provider.getSigner()
      );

      setProgressText("Please complete your order through wallet extension.");
      // Approve the license contract to transfer the license price to the owner
      try {
        await wrappedEthContract.approve(
          song.nft_address,
          ethers.utils.parseEther(song.price.toString())
        );
      } catch (error) {
        if (error.toString().includes("user rejected transaction")) {
          setErrorText("Rejected transaction, please try again");
        } else {
          setErrorText("Something went wrong, please try again");
        }
        setPlacingOrder(false);
        return;
      }

      try {
        await collectibleNFTContract.functions["mint(address)"](
          wrappedEthAddress
        );
      } catch (error) {
        if (
          error
            .toString()
            .includes(
              '"execution reverted: ERC20: transfer amount exceeds balance"'
            )
        ) {
          setErrorText("Insufficient funds, please try again");
        } else {
          setErrorText("Something went wrong, please try again");
        }
        setPlacingOrder(false);
        return;
      }

      setProgressText("Loading...");

      collectibleNFTContract.on("Transfer", (from, to, tokenId) => {
        // When we see the transfer event, then we can add it to the database
        const formData = new FormData();
        formData.append("collection_id", song.id.toString());
        formData.append("token_id", parseInt(tokenId).toString());
        formData.append("owner", auth.user.wallet_address);
        formData.append("contract_address", song.nft_address);
        formData.append("song_name", song.song_name);
        axios
          .post(
            process.env.REACT_APP_BACKEND_URL + "/buy_collectible_nft",
            formData,
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          )
          .then((response) => {
            onCheckoutSuccess();
          });
      });
    };

    return (
      <Modal show={true}>
        <SongCheckoutContainerModal>
          <CloseCircle bg={close_circle} onClick={onCheckoutCloseClick} />
          <ContentColumn>
            <Text bold={true} size={"1.2rem"}>
              Checkout
            </Text>
            <SongCheckoutContainer>
              <SongCheckoutImg src={song.song_cover_s3_link} />
              <Column style={{ justifyContent: "center", paddingLeft: "10%" }}>
                <Text bold={true}>{song.song_name}</Text>
                {artist && <Text>{artist.stage_name}</Text>}
              </Column>
            </SongCheckoutContainer>
            {song.type === SongType.TOKEN && (
              <Row style={{ justifyContent: "flex-start" }}>
                <Text>Token ID:</Text>
                <Text style={{ marginLeft: "auto" }}>#{song.tokenId}</Text>
              </Row>
            )}
            <Row style={{ justifyContent: "flex-start" }}>
              <Text>Your total price is: </Text>
              <CheckoutPriceContainer>
                <img src={eth_icon} />
                {song.price} ETH
              </CheckoutPriceContainer>
            </Row>
            {!placingOrder ? (
              <>
                <SimpleButton
                  style={{ alignSelf: "end" }}
                  onClick={onPlaceOrder}
                >
                  Place Order
                </SimpleButton>
                <BlockChainErrorText>{errorText}</BlockChainErrorText>
              </>
            ) : (
              <>
                <SimpleButton style={{ alignSelf: "end", paddingInline: "7%" }}>
                  <CircularSVG>
                    <CustomCircle
                      cx="15"
                      cy="15"
                      r="10"
                      fill="none"
                      stroke-width="5"
                      stroke-miterlimit="10"
                    />
                  </CircularSVG>
                </SimpleButton>
                <ProgressText>{progressText}</ProgressText>
              </>
            )}
          </ContentColumn>
        </SongCheckoutContainerModal>
      </Modal>
    );
  }
);

export default SongCheckoutModal;
